import moment from 'moment';
import { useCallback, useMemo } from 'react';

import type { MonthlyTaskProps } from '@app/components/Calendars/components/MonthGrid/components/MonthlyTask/types';
import {
  getFirstDayInCurrentWeek,
  getTaskDaysInCurrentWeek,
} from '@app/components/Calendars/utils';
import { useTaskListContext } from '@app/providers/TaskListProvider';

import type { CSSProperties } from 'react';

moment.locale('ru');
const marginSize = 4;

export const useMonthlyTask = (task: MonthlyTaskProps) => {
  const {
    id,
    year,
    month,
    day,
    startTime,
    endTime,
    dimensions,
    title,
    position,
  } = task;

  const {
    hoveredMonthTaskId,
    onChangeHoveredMonthTaskId,
    movedMonthTaskInfo,
    onChangeMovedMonthTaskInfo,
    openPreviewTaskModal,
  } = useTaskListContext();

  const calculateTaskDurations = useCallback(() => {
    const currentTaskDay = moment(`${year}-${month}-${day}`, 'YYYY-M-D');
    const startOfWeek = currentTaskDay
      .clone()
      .startOf('week')
      .format('YYYY-MM-DD');
    const endOfWeek = currentTaskDay.clone().endOf('week').format('YYYY-MM-DD');
    const firstDayInCurrentWeek = getFirstDayInCurrentWeek(
      startTime,
      endTime,
      startOfWeek,
      endOfWeek
    );
    const taskDaysInCurrentWeek = getTaskDaysInCurrentWeek(
      startTime,
      endTime,
      startOfWeek,
      endOfWeek
    );

    const todayIsFirstDayInCurrentWeek =
      moment(`${year}-${month}-${day}`, 'YYYY-M-D').format('YYYY-MM-DD') ===
      firstDayInCurrentWeek;

    return {
      firstDayInCurrentWeek,
      taskDaysInCurrentWeek,
      todayIsFirstDayInCurrentWeek,
    };
  }, [endTime, startTime, day, month, year]);

  const { todayIsFirstDayInCurrentWeek, taskDaysInCurrentWeek } = useMemo(
    calculateTaskDurations,
    [calculateTaskDurations]
  );

  const isHovered = useMemo(
    () => hoveredMonthTaskId === id,
    [hoveredMonthTaskId, id]
  );

  const isDraggabled = useMemo(
    () => movedMonthTaskInfo?.task?.id === id,
    [movedMonthTaskInfo, id]
  );

  const calculateStyles = useCallback((): CSSProperties => {
    const { width: dayWidth } = dimensions;
    let width = dayWidth * 1;
    if (todayIsFirstDayInCurrentWeek) {
      width = dayWidth * taskDaysInCurrentWeek;
    }
    width -= 2 * marginSize;
    const top = (position - 1) * 24 + (position - 1) * 4;
    if (!todayIsFirstDayInCurrentWeek) {
      width = dayWidth;
    }

    return {
      top,
      width,
    };
  }, [
    position,
    dimensions,
    taskDaysInCurrentWeek,
    todayIsFirstDayInCurrentWeek,
  ]);

  const style = useMemo(() => calculateStyles(), [calculateStyles]);

  const taskTitle = useMemo(
    () =>
      todayIsFirstDayInCurrentWeek
        ? `${moment(startTime).format('HH:mm')} ${title}`
        : '',
    [startTime, title, todayIsFirstDayInCurrentWeek]
  );

  const handleMouseEnter = useCallback(() => {
    onChangeHoveredMonthTaskId(id);
  }, [onChangeHoveredMonthTaskId, id]);

  const handleMouseLeave = useCallback(() => {
    onChangeHoveredMonthTaskId(null);
  }, [onChangeHoveredMonthTaskId]);

  const handleMouseDown = useCallback(() => {
    const currentDay = moment(`${year}-${month}-${day}`, 'YYYY-M-D').startOf(
      'day'
    );
    const taskStartTime = moment(startTime).startOf('day');
    const daysFromStart = currentDay.diff(taskStartTime, 'days');
    onChangeMovedMonthTaskInfo({
      task,
      daysFromStart,
      draggabledDay: currentDay.format('YYYY-MM-DD'),
    });
  }, [onChangeMovedMonthTaskInfo, task, day, month, year, startTime]);

  const handleMouseUp = useCallback(() => {
    onChangeMovedMonthTaskInfo(null);
  }, [onChangeMovedMonthTaskInfo]);
  return {
    style,
    taskTitle,
    openPreviewTaskModal,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseDown,
    isDraggabled,
    isHovered,
    todayIsFirstDayInCurrentWeek,
    handleMouseUp,
  };
};
